import React, { useState, useEffect } from "react";
import {
  Button,
  TextField,
  Paper,
  Typography,
  Box,
  Grid,
  CircularProgress,
  Divider,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
} from "@mui/material";
import {
  Send as SendIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
} from "@mui/icons-material";

export default function CC413({ submitForm, declarationInfo }) {
  const [loading, setLoading] = useState(true);
  const [formData, setFormData] = useState(null);
  const [reason, setReason] = useState("");

  useEffect(() => {
    if (declarationInfo) {
      setFormData(declarationInfo);
    }
    setLoading(false);
  }, [declarationInfo]);

  const handleSubmit = () => {
    if (!reason.trim()) {
      alert("Please provide a reason for the amendment");
      return;
    }

    const requestData = {
      reason: reason,
      declaration_data: formData,
    };

    submitForm({ messageType: "CC413", data: requestData });
  };

  // Update handlers for nested fields
  const updateField = (path, value) => {
    setFormData((prev) => {
      const newData = JSON.parse(JSON.stringify(prev));
      const keys = path.split(".");
      let current = newData;

      for (let i = 0; i < keys.length - 1; i++) {
        current = current[keys[i]];
      }
      current[keys[keys.length - 1]] = value;

      return newData;
    });
  };

  // Goods Items handlers
  const addGoodsItem = () => {
    setFormData((prev) => {
      const newData = JSON.parse(JSON.stringify(prev));
      newData.goods_shipment.goods_shipment_items.push({
        declaration_goods_item_number: (
          newData.goods_shipment.goods_shipment_items.length + 1
        ).toString(),
        procedure: {
          requested_procedure: "",
          previous_procedure: "",
          additional_procedures: [],
        },
        previous_documents: [],
        supporting_documents: [],
        commodity: {
          description_of_goods: "",
          harmonized_system_subheading_code: "",
          gross_mass: "",
        },
        packaging: [],
        intrinsic_value: {
          currency: "",
          amount: "",
        },
      });
      return newData;
    });
  };

  const removeGoodsItem = (index) => {
    setFormData((prev) => {
      const newData = JSON.parse(JSON.stringify(prev));
      newData.goods_shipment.goods_shipment_items.splice(index, 1);
      return newData;
    });
  };

  // Additional Procedures handlers
  const addAdditionalProcedure = (itemIndex) => {
    setFormData((prev) => {
      const newData = JSON.parse(JSON.stringify(prev));
      const procedures =
        newData.goods_shipment.goods_shipment_items[itemIndex].procedure
          .additional_procedures;
      procedures.push({
        sequence_number: (procedures.length + 1).toString(),
        additional_procedure: "",
      });
      return newData;
    });
  };

  const removeAdditionalProcedure = (itemIndex, procIndex) => {
    setFormData((prev) => {
      const newData = JSON.parse(JSON.stringify(prev));
      newData.goods_shipment.goods_shipment_items[
        itemIndex
      ].procedure.additional_procedures.splice(procIndex, 1);
      return newData;
    });
  };

  // Previous Documents handlers
  const addPreviousDocument = (itemIndex) => {
    setFormData((prev) => {
      const newData = JSON.parse(JSON.stringify(prev));
      const docs =
        newData.goods_shipment.goods_shipment_items[itemIndex]
          .previous_documents;
      docs.push({
        sequence_number: (docs.length + 1).toString(),
        reference_number: "",
        type: "",
        content: "",
      });
      return newData;
    });
  };

  const removePreviousDocument = (itemIndex, docIndex) => {
    setFormData((prev) => {
      const newData = JSON.parse(JSON.stringify(prev));
      newData.goods_shipment.goods_shipment_items[
        itemIndex
      ].previous_documents.splice(docIndex, 1);
      return newData;
    });
  };

  // Supporting Documents handlers
  const addSupportingDocument = (itemIndex) => {
    setFormData((prev) => {
      const newData = JSON.parse(JSON.stringify(prev));
      const docs =
        newData.goods_shipment.goods_shipment_items[itemIndex]
          .supporting_documents;
      docs.push({
        sequence_number: (docs.length + 1).toString(),
        type: "",
        reference_number: "",
        content: "",
      });
      return newData;
    });
  };

  const removeSupportingDocument = (itemIndex, docIndex) => {
    setFormData((prev) => {
      const newData = JSON.parse(JSON.stringify(prev));
      newData.goods_shipment.goods_shipment_items[
        itemIndex
      ].supporting_documents.splice(docIndex, 1);
      return newData;
    });
  };

  // Transport Documents handlers
  const addTransportDocument = () => {
    setFormData((prev) => {
      const newData = JSON.parse(JSON.stringify(prev));
      const docs = newData.goods_shipment.consignment.transport_documents;
      docs.push({
        sequence_number: (docs.length + 1).toString(),
        reference_number: "",
        type: "",
        content: "",
      });
      return newData;
    });
  };

  const removeTransportDocument = (index) => {
    setFormData((prev) => {
      const newData = JSON.parse(JSON.stringify(prev));
      newData.goods_shipment.consignment.transport_documents.splice(index, 1);
      return newData;
    });
  };

  // Packaging handlers
  const addPackaging = (itemIndex) => {
    setFormData((prev) => {
      const newData = JSON.parse(JSON.stringify(prev));
      const packaging =
        newData.goods_shipment.goods_shipment_items[itemIndex].packaging;
      packaging.push({
        sequence_number: (packaging.length + 1).toString(),
        number_of_packages: "",
      });
      return newData;
    });
  };

  const removePackaging = (itemIndex, packIndex) => {
    setFormData((prev) => {
      const newData = JSON.parse(JSON.stringify(prev));
      newData.goods_shipment.goods_shipment_items[itemIndex].packaging.splice(
        packIndex,
        1
      );
      return newData;
    });
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="200px"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (!formData) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="200px"
      >
        <Typography color="error">Failed to load declaration data</Typography>
      </Box>
    );
  }

  return (
    <div style={{ margin: "20px 0" }}>
      {/* Message Information */}
      <Paper
        sx={{ boxShadow: "none" }}
        style={{
          padding: "20px",
          marginBottom: "20px",
          backgroundColor: "#f5f5f5",
        }}
      >
        <Typography variant="h6" gutterBottom>
          Message Information
        </Typography>
        <Divider style={{ marginBottom: "15px" }} />
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Message Sender"
              value={formData.message.message_sender}
              onChange={(e) =>
                updateField("message.message_sender", e.target.value)
              }
              fullWidth
              required
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Message Recipient"
              value={formData.message.message_recipient}
              onChange={(e) =>
                updateField("message.message_recipient", e.target.value)
              }
              fullWidth
              required
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="body2" color="textSecondary" gutterBottom>
              MRN
            </Typography>
            <Typography variant="body1" style={{ fontWeight: 500 }}>
              {formData.import_operation.mrn || "N/A"}
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* National Declaration */}
      <Paper
        sx={{ boxShadow: "none" }}
        style={{ padding: "20px", marginBottom: "20px" }}
      >
        <Typography variant="h6" gutterBottom>
          National Declaration
        </Typography>
        <Divider style={{ marginBottom: "15px" }} />
        <Grid container spacing={2}>
          <Grid item xs={12} sm={3}>
            <TextField
              label="Custom Type"
              value={formData.national_declaration.custom_type}
              onChange={(e) =>
                updateField("national_declaration.custom_type", e.target.value)
              }
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              label="Declaration Category"
              value={formData.national_declaration.declaration_category}
              onChange={(e) =>
                updateField(
                  "national_declaration.declaration_category",
                  e.target.value
                )
              }
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              label="Declaration Type"
              value={formData.import_operation.declaration_type}
              onChange={(e) =>
                updateField("import_operation.declaration_type", e.target.value)
              }
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              label="Additional Declaration Type"
              value={formData.import_operation.additional_declaration_type}
              onChange={(e) =>
                updateField("import_operation.additional_declaration_type", e.target.value)
              }
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              label="Safety Security Features"
              value={formData.national_declaration.safety_security_features ? "true" : "false"}
              onChange={(e) =>
                updateField(
                  "national_declaration.safety_security_features",
                  e.target.value === "true"
                )
              }
              select
              SelectProps={{
                native: true,
              }}
              fullWidth
            >
              <option value="false">false</option>
              <option value="true">true</option>
            </TextField>
          </Grid>
        </Grid>
      </Paper>

      {/* Customs Office, Importer, Declarant */}
      <Paper
        sx={{ boxShadow: "none" }}
        style={{ padding: "20px", marginBottom: "20px" }}
      >
        <Typography variant="h6" gutterBottom>
          Customs Office & Parties
        </Typography>
        <Divider style={{ marginBottom: "15px" }} />
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Customs Office Reference"
              value={formData.customs_office.reference_number}
              onChange={(e) =>
                updateField("customs_office.reference_number", e.target.value)
              }
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Importer Identification"
              value={formData.importer.identification_number}
              onChange={(e) =>
                updateField("importer.identification_number", e.target.value)
              }
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Declarant Identification"
              value={formData.declarant.identification_number}
              onChange={(e) =>
                updateField("declarant.identification_number", e.target.value)
              }
              fullWidth
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Exporter */}
      <Paper
        sx={{ boxShadow: "none" }}
        style={{ padding: "20px", marginBottom: "20px" }}
      >
        <Typography variant="h6" gutterBottom>
          Exporter Information
        </Typography>
        <Divider style={{ marginBottom: "15px" }} />
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              label="Exporter Name"
              value={formData.goods_shipment.exporter.name}
              onChange={(e) =>
                updateField("goods_shipment.exporter.name", e.target.value)
              }
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Street and Number"
              value={formData.goods_shipment.exporter.address.street_and_number}
              onChange={(e) =>
                updateField(
                  "goods_shipment.exporter.address.street_and_number",
                  e.target.value
                )
              }
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              label="City"
              value={formData.goods_shipment.exporter.address.city}
              onChange={(e) =>
                updateField(
                  "goods_shipment.exporter.address.city",
                  e.target.value
                )
              }
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              label="Postcode"
              value={formData.goods_shipment.exporter.address.postcode}
              onChange={(e) =>
                updateField(
                  "goods_shipment.exporter.address.postcode",
                  e.target.value
                )
              }
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              label="Country"
              value={formData.goods_shipment.exporter.address.country}
              onChange={(e) =>
                updateField(
                  "goods_shipment.exporter.address.country",
                  e.target.value
                )
              }
              fullWidth
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Additional Fiscal Reference */}
      <Paper
        sx={{ boxShadow: "none" }}
        style={{ padding: "20px", marginBottom: "20px" }}
      >
        <Typography variant="h6" gutterBottom>
          Additional Fiscal Reference
        </Typography>
        <Divider style={{ marginBottom: "15px" }} />
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Sequence Number"
              value={formData.goods_shipment.additional_fiscal_reference.sequence_number}
              onChange={(e) =>
                updateField(
                  "goods_shipment.additional_fiscal_reference.sequence_number",
                  e.target.value
                )
              }
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="Role"
              value={formData.goods_shipment.additional_fiscal_reference.role}
              onChange={(e) =>
                updateField(
                  "goods_shipment.additional_fiscal_reference.role",
                  e.target.value
                )
              }
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="VAT Identification Number"
              value={formData.goods_shipment.additional_fiscal_reference.vat_identification_number}
              onChange={(e) =>
                updateField(
                  "goods_shipment.additional_fiscal_reference.vat_identification_number",
                  e.target.value
                )
              }
              fullWidth
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Consignment - Location of Goods & UCR */}
      <Paper
        sx={{ boxShadow: "none" }}
        style={{ padding: "20px", marginBottom: "20px" }}
      >
        <Typography variant="h6" gutterBottom>
          Consignment Information
        </Typography>
        <Divider style={{ marginBottom: "15px" }} />
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Reference Number UCR"
              value={formData.goods_shipment.consignment.reference_number_ucr}
              onChange={(e) =>
                updateField(
                  "goods_shipment.consignment.reference_number_ucr",
                  e.target.value
                )
              }
              fullWidth
            />
          </Grid>
          <Grid item xs={12}>
            <Typography variant="subtitle2" gutterBottom style={{ marginTop: "10px" }}>
              Location of Goods
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Type of Location"
              value={formData.goods_shipment.consignment.location_of_goods.type_of_location}
              onChange={(e) =>
                updateField(
                  "goods_shipment.consignment.location_of_goods.type_of_location",
                  e.target.value
                )
              }
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Qualifier of Identification"
              value={formData.goods_shipment.consignment.location_of_goods.qualifier_of_identification}
              onChange={(e) =>
                updateField(
                  "goods_shipment.consignment.location_of_goods.qualifier_of_identification",
                  e.target.value
                )
              }
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Street and Number"
              value={formData.goods_shipment.consignment.location_of_goods.address.street_and_number}
              onChange={(e) =>
                updateField(
                  "goods_shipment.consignment.location_of_goods.address.street_and_number",
                  e.target.value
                )
              }
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              label="City"
              value={formData.goods_shipment.consignment.location_of_goods.address.city}
              onChange={(e) =>
                updateField(
                  "goods_shipment.consignment.location_of_goods.address.city",
                  e.target.value
                )
              }
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              label="Postcode"
              value={formData.goods_shipment.consignment.location_of_goods.address.postcode}
              onChange={(e) =>
                updateField(
                  "goods_shipment.consignment.location_of_goods.address.postcode",
                  e.target.value
                )
              }
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <TextField
              label="Country"
              value={formData.goods_shipment.consignment.location_of_goods.address.country}
              onChange={(e) =>
                updateField(
                  "goods_shipment.consignment.location_of_goods.address.country",
                  e.target.value
                )
              }
              fullWidth
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Consignment - Transport Documents */}
      <Paper
        sx={{ boxShadow: "none" }}
        style={{ padding: "20px", marginBottom: "20px" }}
      >
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Typography variant="h6">Transport Documents</Typography>
          <Button
            variant="outlined"
            size="small"
            startIcon={<AddIcon />}
            onClick={addTransportDocument}
          >
            Add Document
          </Button>
        </Box>
        <Divider style={{ marginBottom: "15px" }} />
        {formData.goods_shipment.consignment.transport_documents.map(
          (doc, index) => (
            <Paper
              key={index}
              variant="outlined"
              style={{ padding: "15px", marginBottom: "10px" }}
            >
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={1}
              >
                <Chip
                  label={`Document ${index + 1}`}
                  size="small"
                  color="primary"
                />
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => removeTransportDocument(index)}
                >
                  <DeleteIcon />
                </IconButton>
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={3}>
                  <TextField
                    label="Sequence Number"
                    value={doc.sequence_number}
                    onChange={(e) =>
                      updateField(
                        `goods_shipment.consignment.transport_documents.${index}.sequence_number`,
                        e.target.value
                      )
                    }
                    fullWidth
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    label="Type"
                    value={doc.type}
                    onChange={(e) =>
                      updateField(
                        `goods_shipment.consignment.transport_documents.${index}.type`,
                        e.target.value
                      )
                    }
                    fullWidth
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    label="Reference Number"
                    value={doc.reference_number}
                    onChange={(e) =>
                      updateField(
                        `goods_shipment.consignment.transport_documents.${index}.reference_number`,
                        e.target.value
                      )
                    }
                    fullWidth
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    label="Content"
                    value={doc.content}
                    onChange={(e) =>
                      updateField(
                        `goods_shipment.consignment.transport_documents.${index}.content`,
                        e.target.value
                      )
                    }
                    fullWidth
                    size="small"
                  />
                </Grid>
              </Grid>
            </Paper>
          )
        )}
      </Paper>

      {/* Transport and Insurance Cost */}
      <Paper
        sx={{ boxShadow: "none" }}
        style={{ padding: "20px", marginBottom: "20px" }}
      >
        <Typography variant="h6" gutterBottom>
          Transport and Insurance Cost to Destination
        </Typography>
        <Divider style={{ marginBottom: "15px" }} />
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Currency"
              value={formData.goods_shipment.transp_and_insurance_cost_to_dest.currency}
              onChange={(e) =>
                updateField(
                  "goods_shipment.transp_and_insurance_cost_to_dest.currency",
                  e.target.value
                )
              }
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Amount"
              value={formData.goods_shipment.transp_and_insurance_cost_to_dest.amount}
              onChange={(e) =>
                updateField(
                  "goods_shipment.transp_and_insurance_cost_to_dest.amount",
                  e.target.value
                )
              }
              fullWidth
            />
          </Grid>
        </Grid>
      </Paper>

      {/* Goods Items */}
      <Paper
        sx={{ boxShadow: "none" }}
        style={{ padding: "20px", marginBottom: "20px" }}
      >
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={2}
        >
          <Typography variant="h6">Goods Items</Typography>
          <Button
            variant="outlined"
            size="small"
            startIcon={<AddIcon />}
            onClick={addGoodsItem}
          >
            Add Goods Item
          </Button>
        </Box>
        <Divider style={{ marginBottom: "15px" }} />

        {formData.goods_shipment.goods_shipment_items.map((item, itemIndex) => (
          <Accordion key={itemIndex} defaultExpanded={itemIndex === 0}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box display="flex" alignItems="center" gap={1} width="100%">
                <Chip
                  label={`Item ${item.declaration_goods_item_number}`}
                  color="primary"
                />
                <Typography variant="body2" style={{ flexGrow: 1 }}>
                  {item.commodity.description_of_goods || "No description"}
                </Typography>
                <IconButton
                  size="small"
                  color="error"
                  onClick={(e) => {
                    e.stopPropagation();
                    removeGoodsItem(itemIndex);
                  }}
                >
                  <DeleteIcon />
                </IconButton>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                {/* Procedure */}
                <Grid item xs={12}>
                  <Typography variant="subtitle2" gutterBottom>
                    Procedure
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Requested Procedure"
                    value={item.procedure.requested_procedure}
                    onChange={(e) =>
                      updateField(
                        `goods_shipment.goods_shipment_items.${itemIndex}.procedure.requested_procedure`,
                        e.target.value
                      )
                    }
                    fullWidth
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Previous Procedure"
                    value={item.procedure.previous_procedure}
                    onChange={(e) =>
                      updateField(
                        `goods_shipment.goods_shipment_items.${itemIndex}.procedure.previous_procedure`,
                        e.target.value
                      )
                    }
                    fullWidth
                    size="small"
                  />
                </Grid>

                {/* Additional Procedures */}
                <Grid item xs={12}>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Typography variant="subtitle2">
                      Additional Procedures
                    </Typography>
                    <Button
                      variant="text"
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={() => addAdditionalProcedure(itemIndex)}
                    >
                      Add
                    </Button>
                  </Box>
                </Grid>
                {item.procedure.additional_procedures.map((proc, procIndex) => (
                  <Grid item xs={12} key={procIndex}>
                    <Paper variant="outlined" style={{ padding: "10px" }}>
                      <Grid container spacing={2} alignItems="center">
                        <Grid item xs={5}>
                          <TextField
                            label="Sequence"
                            value={proc.sequence_number}
                            onChange={(e) =>
                              updateField(
                                `goods_shipment.goods_shipment_items.${itemIndex}.procedure.additional_procedures.${procIndex}.sequence_number`,
                                e.target.value
                              )
                            }
                            fullWidth
                            size="small"
                          />
                        </Grid>
                        <Grid item xs={5}>
                          <TextField
                            label="Procedure Code"
                            value={proc.additional_procedure}
                            onChange={(e) =>
                              updateField(
                                `goods_shipment.goods_shipment_items.${itemIndex}.procedure.additional_procedures.${procIndex}.additional_procedure`,
                                e.target.value
                              )
                            }
                            fullWidth
                            size="small"
                          />
                        </Grid>
                        <Grid item xs={2}>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() =>
                              removeAdditionalProcedure(itemIndex, procIndex)
                            }
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Grid>
                      </Grid>
                    </Paper>
                  </Grid>
                ))}

                {/* Commodity */}
                <Grid item xs={12}>
                  <Typography variant="subtitle2" gutterBottom>
                    Commodity
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Description of Goods"
                    value={item.commodity.description_of_goods}
                    onChange={(e) =>
                      updateField(
                        `goods_shipment.goods_shipment_items.${itemIndex}.commodity.description_of_goods`,
                        e.target.value
                      )
                    }
                    fullWidth
                    multiline
                    rows={2}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="HS Code"
                    value={item.commodity.harmonized_system_subheading_code}
                    onChange={(e) =>
                      updateField(
                        `goods_shipment.goods_shipment_items.${itemIndex}.commodity.harmonized_system_subheading_code`,
                        e.target.value
                      )
                    }
                    fullWidth
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Gross Mass"
                    value={item.commodity.gross_mass}
                    onChange={(e) =>
                      updateField(
                        `goods_shipment.goods_shipment_items.${itemIndex}.commodity.gross_mass`,
                        e.target.value
                      )
                    }
                    fullWidth
                    size="small"
                  />
                </Grid>

                {/* Previous Documents */}
                <Grid item xs={12}>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Typography variant="subtitle2">
                      Previous Documents
                    </Typography>
                    <Button
                      variant="text"
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={() => addPreviousDocument(itemIndex)}
                    >
                      Add
                    </Button>
                  </Box>
                </Grid>
                {item.previous_documents.map((doc, docIndex) => (
                  <Grid item xs={12} key={docIndex}>
                    <Paper variant="outlined" style={{ padding: "10px" }}>
                      <Box display="flex" justifyContent="flex-end" mb={1}>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() =>
                            removePreviousDocument(itemIndex, docIndex)
                          }
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                      <Grid container spacing={2}>
                        <Grid item xs={6} sm={3}>
                          <TextField
                            label="Sequence"
                            value={doc.sequence_number}
                            onChange={(e) =>
                              updateField(
                                `goods_shipment.goods_shipment_items.${itemIndex}.previous_documents.${docIndex}.sequence_number`,
                                e.target.value
                              )
                            }
                            fullWidth
                            size="small"
                          />
                        </Grid>
                        <Grid item xs={6} sm={3}>
                          <TextField
                            label="Type"
                            value={doc.type}
                            onChange={(e) =>
                              updateField(
                                `goods_shipment.goods_shipment_items.${itemIndex}.previous_documents.${docIndex}.type`,
                                e.target.value
                              )
                            }
                            fullWidth
                            size="small"
                          />
                        </Grid>
                        <Grid item xs={6} sm={3}>
                          <TextField
                            label="Reference"
                            value={doc.reference_number}
                            onChange={(e) =>
                              updateField(
                                `goods_shipment.goods_shipment_items.${itemIndex}.previous_documents.${docIndex}.reference_number`,
                                e.target.value
                              )
                            }
                            fullWidth
                            size="small"
                          />
                        </Grid>
                        <Grid item xs={6} sm={3}>
                          <TextField
                            label="Content"
                            value={doc.content}
                            onChange={(e) =>
                              updateField(
                                `goods_shipment.goods_shipment_items.${itemIndex}.previous_documents.${docIndex}.content`,
                                e.target.value
                              )
                            }
                            fullWidth
                            size="small"
                          />
                        </Grid>
                      </Grid>
                    </Paper>
                  </Grid>
                ))}

                {/* Supporting Documents */}
                <Grid item xs={12}>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Typography variant="subtitle2">
                      Supporting Documents
                    </Typography>
                    <Button
                      variant="text"
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={() => addSupportingDocument(itemIndex)}
                    >
                      Add
                    </Button>
                  </Box>
                </Grid>
                {item.supporting_documents.map((doc, docIndex) => (
                  <Grid item xs={12} key={docIndex}>
                    <Paper variant="outlined" style={{ padding: "10px" }}>
                      <Box display="flex" justifyContent="flex-end" mb={1}>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() =>
                            removeSupportingDocument(itemIndex, docIndex)
                          }
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                      <Grid container spacing={2}>
                        <Grid item xs={6} sm={3}>
                          <TextField
                            label="Sequence"
                            value={doc.sequence_number}
                            onChange={(e) =>
                              updateField(
                                `goods_shipment.goods_shipment_items.${itemIndex}.supporting_documents.${docIndex}.sequence_number`,
                                e.target.value
                              )
                            }
                            fullWidth
                            size="small"
                          />
                        </Grid>
                        <Grid item xs={6} sm={3}>
                          <TextField
                            label="Type"
                            value={doc.type}
                            onChange={(e) =>
                              updateField(
                                `goods_shipment.goods_shipment_items.${itemIndex}.supporting_documents.${docIndex}.type`,
                                e.target.value
                              )
                            }
                            fullWidth
                            size="small"
                          />
                        </Grid>
                        <Grid item xs={6} sm={3}>
                          <TextField
                            label="Reference"
                            value={doc.reference_number}
                            onChange={(e) =>
                              updateField(
                                `goods_shipment.goods_shipment_items.${itemIndex}.supporting_documents.${docIndex}.reference_number`,
                                e.target.value
                              )
                            }
                            fullWidth
                            size="small"
                          />
                        </Grid>
                        <Grid item xs={6} sm={3}>
                          <TextField
                            label="Content"
                            value={doc.content}
                            onChange={(e) =>
                              updateField(
                                `goods_shipment.goods_shipment_items.${itemIndex}.supporting_documents.${docIndex}.content`,
                                e.target.value
                              )
                            }
                            fullWidth
                            size="small"
                          />
                        </Grid>
                      </Grid>
                    </Paper>
                  </Grid>
                ))}

                {/* Packaging */}
                <Grid item xs={12}>
                  <Box
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Typography variant="subtitle2">Packaging</Typography>
                    <Button
                      variant="text"
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={() => addPackaging(itemIndex)}
                    >
                      Add
                    </Button>
                  </Box>
                </Grid>
                {item.packaging.map((pack, packIndex) => (
                  <Grid item xs={12} key={packIndex}>
                    <Paper variant="outlined" style={{ padding: "10px" }}>
                      <Grid container spacing={2} alignItems="center">
                        <Grid item xs={5}>
                          <TextField
                            label="Sequence"
                            value={pack.sequence_number}
                            onChange={(e) =>
                              updateField(
                                `goods_shipment.goods_shipment_items.${itemIndex}.packaging.${packIndex}.sequence_number`,
                                e.target.value
                              )
                            }
                            fullWidth
                            size="small"
                          />
                        </Grid>
                        <Grid item xs={5}>
                          <TextField
                            label="Number of Packages"
                            value={pack.number_of_packages}
                            onChange={(e) =>
                              updateField(
                                `goods_shipment.goods_shipment_items.${itemIndex}.packaging.${packIndex}.number_of_packages`,
                                e.target.value
                              )
                            }
                            fullWidth
                            size="small"
                          />
                        </Grid>
                        <Grid item xs={2}>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() =>
                              removePackaging(itemIndex, packIndex)
                            }
                          >
                            <DeleteIcon />
                          </IconButton>
                        </Grid>
                      </Grid>
                    </Paper>
                  </Grid>
                ))}

                {/* Intrinsic Value */}
                <Grid item xs={12}>
                  <Typography variant="subtitle2" gutterBottom>
                    Intrinsic Value
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Currency"
                    value={item.intrinsic_value.currency}
                    onChange={(e) =>
                      updateField(
                        `goods_shipment.goods_shipment_items.${itemIndex}.intrinsic_value.currency`,
                        e.target.value
                      )
                    }
                    fullWidth
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Amount"
                    value={item.intrinsic_value.amount}
                    onChange={(e) =>
                      updateField(
                        `goods_shipment.goods_shipment_items.${itemIndex}.intrinsic_value.amount`,
                        e.target.value
                      )
                    }
                    fullWidth
                    size="small"
                  />
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
        ))}
      </Paper>

      {/* Reason for Amendment */}
      <Paper
        sx={{ boxShadow: "none" }}
        style={{ padding: "20px", marginBottom: "20px" }}
      >
        <Typography variant="h6" gutterBottom>
          Reason for Amendment
        </Typography>
        <Divider style={{ marginBottom: "15px" }} />
        <TextField
          label="Reason"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          fullWidth
          multiline
          rows={4}
          placeholder="Enter reason for the amendment declaration..."
          required
        />
      </Paper>

      {/* Submit Button */}
      <Box display="flex" gap={2} justifyContent="center" mt={3}>
        <Button
          variant="contained"
          color="primary"
          startIcon={<SendIcon />}
          onClick={handleSubmit}
          size="large"
        >
          Submit CC413 Amendment
        </Button>
      </Box>
    </div>
  );
}
